import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'motion/react'
import { CheckCircle2, ChevronLeft, FileText, Link2, Lock, PenLine } from 'lucide-react'
import { listApplications } from '../api/applicant'
import type { ApplicationWithDocuments } from '../api/types'
import { PageShell } from '../components/layout/PageShell'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { RowTile } from '../components/ui/RowTile'
import { MetaValue } from '../components/ui/MetaValue'
import { EntryCard } from '../components/ui/EntryCard'
import { REGIONS } from '../constants/processingBreakdown'
import { DOCUMENT_SLOT_LABELS } from '../constants/documentSlots'
import { applicationReference } from './MyApplicationsScreen'

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.4, ease: 'easeOut' as const },
  }),
}

// ---------------------------------------------------------------------------
// Read-only building blocks (shared visual language with the Review screen)
// ---------------------------------------------------------------------------
function ReadOnlyField({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  /** Optional leading glyph in the value row (e.g. the "Linked to" link icon). */
  icon?: ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-md)',
          color: 'var(--color-text-primary)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {icon}
        {value || '—'}
      </span>
    </div>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h3
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-title-md)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-primary)',
        margin: '0 0 var(--spacing-8) 0',
      }}
    >
      {title}
    </h3>
  )
}

function SubHeader({ title }: { title: string }) {
  return (
    <h4
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-label-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        margin: '0 0 var(--spacing-3) 0',
      }}
    >
      {title}
    </h4>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Loosely-typed rows persisted by CompanyStructureScreen; legacy submissions
// may carry old fields (name instead of firstName/lastName, sharesOwned…).
interface OwnerRow {
  kind?: string
  firstName?: string
  lastName?: string
  legalCompanyName?: string
  ownershipPct?: number
  nationality?: string
  dob?: string
  passportNumber?: string
  placeOfIssue?: string
  registeredAddress?: string
  countryOfIncorporation?: string
  registrationNumber?: string
  sharesOwned?: string
  shareClass?: string
}

interface DirectorRow {
  name?: string
  firstName?: string
  lastName?: string
  dob?: string
  nationality?: string
  passportNumber?: string
  placeOfIssue?: string
  isAuthorisedSignatory?: boolean
  linkedOwnerName?: string
}

// ---------------------------------------------------------------------------
// ApplicationDetailScreen — the complete read-only record of one application:
// every section and every field, for submitted and in-progress applications.
// ---------------------------------------------------------------------------
export function ApplicationDetailScreen() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [apps, setApps] = useState<ApplicationWithDocuments[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    listApplications()
      .then((res) => setApps(res.applications))
      .catch(() => setLoadFailed(true))
  }, [])

  const app = apps?.find((a) => a.id === id) ?? null

  if (apps === null || loadFailed || !app) {
    return (
      <PageShell>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <p style={{ fontFamily: 'var(--font-body)', color: loadFailed ? 'var(--color-error-text)' : 'var(--color-text-secondary)' }}>
            {loadFailed
              ? 'Something went wrong loading this application. Please refresh the page.'
              : apps === null
                ? 'Loading application…'
                : 'Application not found.'}
          </p>
          {apps !== null && (
            <Button variant="tertiary" onClick={() => navigate('/applications')} style={{ gap: 'var(--spacing-1)', marginTop: 'var(--spacing-4)' }}>
              <ChevronLeft size={18} strokeWidth={2.2} />
              Back to My Applications
            </Button>
          )}
        </div>
      </PageShell>
    )
  }

  const fd = app.form_data ?? {}
  const submitted = app.status === 'submitted'

  // Company details -------------------------------------------------------
  const legacyAddress = [fd.registeredAddressStreet, fd.registeredAddressCity, fd.registeredAddressCountry]
    .filter(Boolean)
    .join(', ')
  const regAddress = (fd.registeredOfficeAddress as string) || legacyAddress

  // License ----------------------------------------------------------------
  const noLicence = fd.noLicence as boolean | undefined

  // Ownership --------------------------------------------------------------
  const owners = Array.isArray(fd.owners) ? (fd.owners as OwnerRow[]) : []
  const directors = Array.isArray(fd.directors) ? (fd.directors as DirectorRow[]) : []

  function directorDisplayName(d: DirectorRow, i: number): string {
    const split = `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim()
    return split || d.name || `Director ${i + 1}`
  }

  // Processing breakdown ---------------------------------------------------
  const regionBreakdown = REGIONS.map((r) => ({ label: r.label, value: Number(fd[r.key]) || 0 })).filter(
    (e) => e.value > 0,
  )
  const monthlyVolume = Number(fd.estimated_monthly_volume) || 0

  let cardIndex = 0

  return (
    <PageShell>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <div style={{ display: 'flex', marginBottom: 'var(--spacing-4)' }}>
            <Button
              variant="tertiary"
              onClick={() => navigate('/applications')}
              style={{ gap: 'var(--spacing-1)', flexShrink: 0, marginLeft: 'calc(-1 * var(--spacing-2))' }}
            >
              <ChevronLeft size={18} strokeWidth={2.2} />
              My Applications
            </Button>
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              // Same status-pill recipe as My applications: opaque 50-level
              // fill, border and text on the DARK stop. The mid stop is never
              // text, and the tints these used to carry were sub-AA at 12px.
              background: submitted ? 'var(--color-success-surface)' : 'var(--color-ai-badge-surface)',
              border: `1px solid ${submitted ? 'var(--color-success-text)' : 'var(--color-action-primary)'}`,
              borderRadius: 'var(--radius-full)',
              padding: '6px 14px',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            {submitted ? (
              <CheckCircle2 size={14} style={{ color: 'var(--color-success-text)' }} aria-hidden="true" />
            ) : (
              <PenLine size={14} style={{ color: 'var(--color-action-primary)' }} aria-hidden="true" />
            )}
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                fontWeight: 'var(--weight-semibold)',
                color: submitted ? 'var(--color-success-text)' : 'var(--color-action-primary)',
              }}
            >
              {submitted ? 'Submitted' : 'In progress'}
            </span>
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-lg)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text-primary)',
              margin: '0 0 var(--spacing-1) 0',
            }}
          >
            {(fd.legalCompanyName as string) || 'Merchant Application'}
          </h1>
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-secondary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <Lock size={12} aria-hidden="true" />
            Read-only view · Reference: {applicationReference(app.id)}
            {submitted
              ? ` · Submitted ${formatDate(app.submitted_at)}`
              : ` · Last saved ${formatDate(app.last_updated_at)}`}
          </p>
        </div>

        {/* Contact Details */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-6)' }}>
          <Card padding="lg">
            <SectionHeader title="Your Contact Details" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <ReadOnlyField label="First name" value={(fd.firstName as string) || ''} />
              <ReadOnlyField label="Last name" value={(fd.lastName as string) || ''} />
              <ReadOnlyField label="Job title" value={(fd.jobTitle as string) || ''} />
              <ReadOnlyField label="Company email address" value={(fd.authEmail as string) || ''} />
              <ReadOnlyField label="Company website" value={(fd.companyWebsite as string) || ''} />
            </div>
          </Card>
        </motion.div>

        {/* Company Details */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-6)' }}>
          <Card padding="lg">
            <SectionHeader title="Company Details" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <ReadOnlyField label="Legal company name" value={(fd.legalCompanyName as string) || ''} />
              <ReadOnlyField label="Company registration number" value={(fd.registrationNumber as string) || ''} />
              <ReadOnlyField label="Date of incorporation" value={(fd.incorporationDate as string) || ''} />
              <ReadOnlyField label="Country of incorporation" value={(fd.countryOfIncorporation as string) || ''} />
              <ReadOnlyField label="Registered office country" value={(fd.registeredAddressCountry as string) || ''} />
              <ReadOnlyField label="Registered office address" value={regAddress} />
            </div>
          </Card>
        </motion.div>

        {/* Company License */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-6)' }}>
          <Card padding="lg">
            <SectionHeader title="Company License" />
            {noLicence ? (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-secondary)' }}>
                No licence has been provided.
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <ReadOnlyField label="License name" value={(fd.licenseType as string) || ''} />
                <ReadOnlyField label="License number" value={(fd.licenseNumber as string) || ''} />
                {/* Legacy fields, removed from the form 22-07 — shown only when an
                    older application recorded them. */}
                {Boolean(fd.licenseRegulator) && (
                  <ReadOnlyField label="Name of regulator" value={(fd.licenseRegulator as string) || ''} />
                )}
                {Boolean(fd.licenseExpiryDate) && (
                  <ReadOnlyField label="License expiration date" value={(fd.licenseExpiryDate as string) || ''} />
                )}
              </div>
            )}
          </Card>
        </motion.div>

        {/* Company Ownership */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-6)' }}>
          <Card padding="lg">
            <SectionHeader title="Company Ownership" />
            <div style={{ marginBottom: 'var(--spacing-6)' }}>
              <SubHeader title="Beneficial Owners" />
              {owners.length === 0 ? (
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-tertiary)' }}>
                  Not provided
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                  {owners.map((o, i) => {
                    const isHolding = o.kind === 'holding'
                    const title = isHolding
                      ? o.legalCompanyName || `Owner ${i + 1}`
                      : `${o.firstName ?? ''} ${o.lastName ?? ''}`.trim() || `Owner ${i + 1}`
                    return (
                      <EntryCard key={i} title={title} meta={`${o.ownershipPct ?? 0}%`}>
                        {isHolding ? (
                          <>
                            <ReadOnlyField label="Type" value="Holding company" />
                            <ReadOnlyField label="Country of incorporation" value={o.countryOfIncorporation || ''} />
                            <ReadOnlyField label="Registration number" value={o.registrationNumber || ''} />
                          </>
                        ) : (
                          <>
                            <ReadOnlyField label="Type" value="Individual" />
                            <ReadOnlyField label="Date of birth" value={o.dob || ''} />
                            <ReadOnlyField label="Nationality" value={o.nationality || ''} />
                            <ReadOnlyField label="Passport number" value={o.passportNumber || ''} />
                            <ReadOnlyField label="Place of issue" value={o.placeOfIssue || ''} />
                            <ReadOnlyField label="Registered address" value={o.registeredAddress || ''} />
                          </>
                        )}
                        {/* Legacy fields from applications recorded before 22-07. */}
                        {Boolean(o.sharesOwned) && <ReadOnlyField label="Shares owned" value={o.sharesOwned || ''} />}
                        {Boolean(o.shareClass) && <ReadOnlyField label="Share class" value={o.shareClass || ''} />}
                      </EntryCard>
                    )
                  })}
                </div>
              )}
            </div>
            <div>
              <SubHeader title="Directors" />
              {directors.length === 0 ? (
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-tertiary)' }}>
                  Not provided
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                  {directors.map((d, i) => (
                    <EntryCard
                      key={i}
                      title={directorDisplayName(d, i)}
                      meta={d.isAuthorisedSignatory ? 'Authorised signatory' : undefined}
                    >
                      <ReadOnlyField label="Date of birth" value={d.dob || ''} />
                      <ReadOnlyField label="Nationality" value={d.nationality || ''} />
                      <ReadOnlyField label="Passport number" value={d.passportNumber || ''} />
                      <ReadOnlyField label="Place of issue" value={d.placeOfIssue || ''} />
                      {d.linkedOwnerName && (
                        <ReadOnlyField
                          label="Linked to"
                          value={d.linkedOwnerName}
                          icon={<Link2 size={15} aria-hidden="true" style={{ color: 'var(--color-action-primary)', flexShrink: 0 }} />}
                        />
                      )}
                    </EntryCard>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Processing Breakdown */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-6)' }}>
          <Card padding="lg">
            <SectionHeader title="Processing Breakdown" />
            {regionBreakdown.length === 0 && monthlyVolume === 0 ? (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-tertiary)' }}>
                Not provided
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {regionBreakdown.map((e) => (
                  <RowTile key={e.label} style={{ justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-primary)' }}>
                      {e.label}
                    </span>
                    <MetaValue>{e.value}%</MetaValue>
                  </RowTile>
                ))}
                <ReadOnlyField
                  label="Estimated monthly volume (USD)"
                  value={monthlyVolume > 0 ? `$${monthlyVolume.toLocaleString('en-US')}` : ''}
                />
              </div>
            )}
          </Card>
        </motion.div>

        {/* Uploaded Documents */}
        <motion.div custom={cardIndex++} variants={cardVariants} initial="hidden" animate="visible" style={{ marginBottom: 'var(--spacing-12)' }}>
          <Card padding="lg">
            <SectionHeader title="Uploaded Documents" />
            {app.documents.length === 0 ? (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-tertiary)' }}>
                No documents uploaded
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {app.documents.map((doc) => (
                  <RowTile key={doc.id} style={{ gap: 'var(--spacing-3)' }}>
                    <FileText size={18} style={{ color: 'var(--color-action-primary)', flexShrink: 0 }} aria-hidden="true" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-primary)' }}>
                        {DOCUMENT_SLOT_LABELS[doc.slot as keyof typeof DOCUMENT_SLOT_LABELS] ?? doc.slot}
                        {doc.slot_index != null ? ` #${doc.slot_index + 1}` : ''}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-label-sm)',
                          color: 'var(--color-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.filename}
                      </div>
                    </div>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-label-sm)',
                        fontWeight: 'var(--weight-semibold)',
                        color: 'var(--color-success-text)',
                        flexShrink: 0,
                      }}
                    >
                      <CheckCircle2 size={14} aria-hidden="true" />
                      Uploaded
                    </span>
                  </RowTile>
                ))}
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </PageShell>
  )
}
